import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { EventType, type AccountInfo, type EventMessage } from '@azure/msal-browser';
import App from '@/App';
import { msalInstance, ensureMsalInitialized } from '@/auth/index';
import './index.css';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found in index.html');

// Initialize MSAL and process any pending auth redirect BEFORE rendering (the portal's D1/D8 fix):
// the account cache is populated before any component can fire a gated /api call, so getApiToken()
// never misses the signed-in account on the post-Entra return navigation.
ensureMsalInitialized()
  .then(() => msalInstance.handleRedirectPromise())
  .catch((e) => {
    // eslint-disable-next-line no-console
    console.warn('MSAL init/redirect processing failed (login wall will catch unauthenticated state)', e);
  })
  .finally(() => {
    // Pin the active account so getApiToken() mints the bearer for the SIGNED-IN identity rather
    // than getAllAccounts()[0] (which can be a stale/foreign cached account).
    try {
      if (!msalInstance.getActiveAccount()) {
        const first = msalInstance.getAllAccounts()[0];
        if (first) msalInstance.setActiveAccount(first);
      }
      msalInstance.addEventCallback((event: EventMessage) => {
        if (
          (event.eventType === EventType.LOGIN_SUCCESS ||
            event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
          event.payload &&
          'account' in event.payload &&
          event.payload.account
        ) {
          msalInstance.setActiveAccount(event.payload.account as AccountInfo);
        }
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('MSAL active-account wiring failed', e);
    }
    createRoot(rootEl).render(
      <StrictMode>
        <MsalProvider instance={msalInstance}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </MsalProvider>
      </StrictMode>,
    );
  });
